import React, { useContext, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle, Clock, XCircle, Activity, FileText, ListTodo } from 'lucide-react';
import AuthContext from '../../../../context/AuthContext';
import riskIdentifierStatsService from '../../../../services/riskIdentifierStatsService';
import RecentHistoriesSection from './RecentHistoriesSection';

export default function RiskIdentifierDashboardHome() {

    const { authTokens } = useContext(AuthContext);
    const [dashboardData, setDashboardData] = useState(null);
    const [recentHistories, setRecentHistories] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDashboardData = async () => {
            if (!authTokens.access) {
            console.log('❌ No access token available');
            setLoading(false);
            return;
            }

            try {
                setLoading(true);
                console.log('🚀 Starting admin dashboard data fetch...');

                // Fetch all data in parallel
                const [statsResult, historiesResult] = await Promise.all([
                    riskIdentifierStatsService.getRiskIdentifierDashboardStats(authTokens.access),
                    riskIdentifierStatsService.getRiskIdentifierRecentHistories(authTokens.access)
                ]);

                if (statsResult.success) {
                    setDashboardData(statsResult.data);
                    console.log('✅ Dashboard stats loaded:', statsResult.data);
                }


                if (historiesResult.success) {
                    setRecentHistories(historiesResult.data);
                    console.log('✅ Recent histories loaded:', historiesResult.data);
                }

                console.log('✅ All API data loaded successfully!');
            } catch (error) {
                console.error('❌ Error fetching dashboard data:', error);
            } finally {
            setLoading(false);
            }
        };

        fetchDashboardData();
    }, [authTokens.access]);

    if (loading) {
    return (
        <div className="flex items-center justify-center h-64">
        <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement du tableau de bord...</p>
        </div>
        </div>
    );
    }

    const stats = dashboardData || {};
    const riskStatus = stats.risk_status || {};

    return (
        <div className="space-y-6">
            {/* Risk Status Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Risques totaux</p>
                            <p className="text-2xl font-semibold text-gray-900">{riskStatus.total_risks || 0}</p>
                        </div>
                        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                            <AlertTriangle className="h-6 w-6 text-red-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Risques approuvés</p>
                            <p className="text-2xl font-semibold text-gray-900">{riskStatus.approved || 0}</p>
                            <p className="text-xs text-green-600 mt-1">{riskStatus.approved_percentage?.toFixed(1) || 0}%</p>
                        </div>
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                            <CheckCircle className="h-6 w-6 text-green-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Risques en attente</p>
                            <p className="text-2xl font-semibold text-gray-900">{riskStatus.pending || 0}</p>
                            <p className="text-xs text-orange-600 mt-1">{riskStatus.pending_percentage?.toFixed(1) || 0}%</p>
                        </div>
                        <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                            <Clock className="h-6 w-6 text-orange-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Risques rejetés</p>
                            <p className="text-2xl font-semibold text-gray-900">{riskStatus.rejected || 0}</p>
                            <p className="text-xs text-red-600 mt-1">{riskStatus.rejected_percentage?.toFixed(1) || 0}%</p>
                        </div>
                        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                            <XCircle className="h-6 w-6 text-red-600" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Tasks & Processes Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                    <div className="flex items-center mb-4">
                        <ListTodo className="h-5 w-5 text-blue-600 mr-2" />
                        <h3 className="text-lg font-semibold text-gray-900">Mes tâches</h3>
                    </div>
                    <div className="space-y-3">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Tâches en attente</span>
                            <span className="font-semibold text-orange-600">{stats.tasks_in_pending || 0}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Tâches en cours</span>
                            <span className="font-semibold text-blue-600">{stats.tasks_in_progress || 0}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Tâches acceptées</span>
                            <span className="font-semibold text-green-600">{stats.tasks_in_accepted || 0}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Tâches rejetées</span>
                            <span className="font-semibold text-red-600">{stats.tasks_in_rejected || 0}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Tâches complétées</span>
                            <span className="font-semibold text-teal-600">{stats.tasks_completed || 0}</span>
                        </div>
                    </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                    <div className="flex items-center mb-4">
                        <FileText className="h-5 w-5 text-teal-600 mr-2" />
                        <h3 className="text-lg font-semibold text-gray-900">Processus</h3>
                    </div>
                    <div className="space-y-3">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Processus publiés</span>
                            <span className="font-semibold text-gray-900">{stats.published_processes || 0}</span>
                        </div>
                        <div className="pt-4">
                            <div className="text-center p-6 bg-gray-50 rounded-lg">
                                <p className="text-2xl font-bold text-gray-900">{stats.published_processes || 0}</p>
                                <p className="text-sm text-gray-600 mt-1">Processus disponibles</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recent Histories Section */}
            <RecentHistoriesSection data={recentHistories} loading={loading} />
        </div>
    );
}